import { DateTime } from 'luxon';
import { useCallback, useMemo, useRef, useState } from 'react';
import { debounce } from 'utils/delay';
import { CalendarEvent } from '../types';
import {
	CalendarContext,
	CalendarContextValue,
	CalendarLoadEventsFilter,
	LoadEventFn,
} from './context';
import { useCalendarState } from '../hooks/use-calendar-state';

export type CalendarProviderProps = Pick<
	CalendarContextValue,
	| 'timezone'
	| 'locale'
	| 'events'
	| 'loadEventsStatus'
	| 'onEventClick'
	| 'members'
	| 'exportLink'
> & {
	calendarId: string;
	loadEvents: (filter: CalendarLoadEventsFilter) => void;
	children?: React.ReactNode;
};

export const CalendarProvider: React.FC<CalendarProviderProps> = ({
	timezone = 'America/Chicago',
	locale = 'en-US',
	children,
	events: rawEvents,
	loadEventsStatus,
	loadEvents: loadEventsFn,
	members,
	onEventClick,
	exportLink,
	calendarId,
}) => {
	const loadEventsFilterRef = useRef<CalendarLoadEventsFilter>({
		startDate: DateTime.local({ zone: timezone }),
		endDate: DateTime.local({ zone: timezone }),
	});

	const defaultDisplayed = DateTime.local({ zone: timezone });
	const {
		filter,
		view: currentView,
		displayed,
		agendaFilterOption,
		agendaCustomDates,
		setFilter,
		setView: setCurrentView,
		setDisplayed,
		setAgendaFilterOption,
		setAgendaCustomDates,
	} = useCalendarState(calendarId, timezone, defaultDisplayed);

	const [selected, setSelected] = useState<DateTime | null>(null);

	const formatDate = useCallback(
		(date: DateTime, format: string) =>
			date.setLocale(locale).toFormat(format),
		[locale]
	);

	const [events, eventsMap] = useMemo(() => {
		const events = rawEvents
			.map(event => {
				return {
					...event,
					startDate: event.startDateTime
						? event.startDate.setZone(timezone)
						: event.startDate,
					endDate: event.endDateTime
						? event.endDate.setZone(timezone)
						: event.endDate,
					startDateTime: event.startDateTime
						? event.startDateTime.setZone(timezone)
						: null,
					endDateTime: event.endDateTime
						? event.endDateTime.setZone(timezone)
						: null,
				};
			})
			.sort((a, b) => {
				if (a.startDateTime && b.startDateTime) {
					return (
						a.startDateTime.toMillis() - b.startDateTime.toMillis()
					);
				}

				return -1;
			});

		// Group events by day and hours (map[iso-date-format] = events)
		const eventsMap = events.reduce((carry, event) => {
			const dayKey = (event.startDateTime || event.startDate).toFormat(
				'yyyy-MM-dd'
			);

			if (!carry.has(dayKey)) {
				carry.set(dayKey, []);
			}

			carry.get(dayKey)?.push(event);

			return carry;
		}, new Map<string, CalendarEvent[]>());

		return [events, eventsMap];
	}, [rawEvents]);

	const getNextEventFilter = (options: CalendarLoadEventsFilter) => {
		const { current: currentFilter } = loadEventsFilterRef;

		// Always fetch an extra day before and after for boundary events
		const expandedFilter: CalendarLoadEventsFilter = {
			startDate: options.startDate.minus({ days: 1 }),
			endDate: options.endDate.plus({ days: 1 }),
		};

		const nextFilter = {
			...currentFilter,
			...expandedFilter,
		};

		const { startDate, endDate } = nextFilter;

		const incomingDatesAreIn =
			currentFilter &&
			startDate >= currentFilter.startDate &&
			endDate <= currentFilter.endDate;

		if (incomingDatesAreIn) {
			return;
		}

		loadEventsFilterRef.current = nextFilter;

		return nextFilter;
	};

	const handleLoadEvents = useCallback(
		debounce((options: CalendarLoadEventsFilter) => {
			const nextFilter = getNextEventFilter(options);

			if (!nextFilter) {
				return;
			}

			loadEventsFn({ ...nextFilter });
		}, 500),
		[loadEventsFn]
	);

	const loadEvents: LoadEventFn = useCallback(
		argument => {
			handleLoadEvents(
				typeof argument === 'function'
					? argument(loadEventsFilterRef.current)
					: argument
			);
		},
		[loadEventsFilterRef.current]
	);

	const value: CalendarContextValue = {
		timezone,
		locale,
		displayed,
		setDisplayed,
		selected,
		setSelected,
		currentView,
		setCurrentView,
		formatDate,
		events,
		eventsMap,
		members,
		loadEvents,
		onEventClick,
		loadEventsStatus,
		filter,
		setFilter,
		agendaFilterOption,
		setAgendaFilterOption,
		agendaCustomDates,
		setAgendaCustomDates,
		exportLink,
	};

	const renderedContent = useMemo(() => children, []);

	return (
		<CalendarContext.Provider value={value}>
			{renderedContent}
		</CalendarContext.Provider>
	);
};
